/**
 * The tenancy agreement, and what a signature on it is.
 *
 * Until now this product recorded the *terms* of a letting and produced no *agreement* anybody
 * signed. A manager typed the rent, the cycle and the charges into the tenancy form, and then went
 * away and typed all of it a second time into a word processor — which is the moment the document
 * and the ledger begin to disagree, and the disagreement is only ever discovered by the person
 * least able to do anything about it. So an agreement here is **generated from a tenancy** and
 * there is deliberately nowhere in the product to retype the money.
 *
 * The whole file is pure. Nothing reads a clock, nothing reaches a database, nothing hashes —
 * `@rentorng/core` is imported by a React Native bundle and by the browser app, so the hashing
 * lives in `@rentorng/core/crypto` beside the passwords and is called by the API. What is here is
 * the composition, the arithmetic check, the state machine and the divergence check, which are the
 * four things that have to read identically on a screen, on a page and in a test.
 *
 * ---
 *
 * ## Four decisions worth arguing with before changing anything
 *
 * **1. The agreement carries its own copy of the terms, and never a reference to them.**
 *
 * `AgreementTerms` below is a *snapshot*, taken from the tenancy at the moment the agreement is
 * raised and written into a column of its own. The obvious alternative — render the agreement from
 * the live tenancy every time somebody looks at it — is the one thing a signed document may never
 * do. A tenant signs a rent of one figure; the office corrects a typo on the tenancy a fortnight
 * later; and every copy of the agreement, including the one already signed, now says something
 * nobody agreed to. Evidence that changes when the record behind it changes is not evidence.
 *
 * **2. So a tenancy that moves does not move the agreement. It *diverges* from it.**
 *
 * `divergenceOf()` compares the frozen snapshot against the tenancy as it stands now and returns
 * the fields that have moved, with both figures. It is computed on every read and stored nowhere,
 * because a stored answer to "are these two still the same" goes stale the instant either side is
 * written and is then worse than no answer at all.
 *
 * What the agency sees is therefore honest in both directions: the signed document still says what
 * was signed, and the screen says plainly that the tenancy has since changed and in what respect.
 * **The repair is a new agreement, never an edit** — `supersedes`, one press, the same generation
 * from the same tenancy. An amendment to a signed document is a second document; that is what an
 * amendment *is* outside software, and pretending otherwise inside it is how a half-signed
 * agreement quietly becomes a fully-signed agreement to different terms.
 *
 * **3. What a signature is, and what it is not.**
 *
 * A signature here is: **a name typed by the person themselves, the moment they typed it, the
 * address they were signed in as, the network they came from, the browser they used, and the
 * fingerprint of the exact document that was in front of them.** All six are recorded, and the
 * last is what makes the rest worth anything.
 *
 * It is deliberately **not a drawn mark**. A squiggle on a touchpad is a picture; it can be copied
 * from any earlier document in a second, it binds to nothing, and its only real function is to
 * *look* like a wet signature — which invites everybody involved to believe something about it
 * that this product cannot support. The typed name plus the trail is less familiar and strictly
 * more evidence, and it is the version that can be checked years later by comparison rather than
 * by argument.
 *
 * **This product does not claim, anywhere, that a signature recorded here is equivalent to a
 * signature written by hand, that it is witnessed, that it is notarised, or that it satisfies any
 * particular rule about electronic signatures.** It claims exactly what it can show: that somebody
 * holding this account's credential typed this name at this instant against a document with this
 * fingerprint. `SIGNATURE_STATEMENT` below is the sentence printed on the document, and it is
 * written to promise no more than that.
 *
 * **4. The fingerprint is of the agreement, not of the file.**
 *
 * The tempting thing to hash is the PDF. It cannot be: the PDF contains the signature block, so it
 * changes every time somebody signs it, and hashing it would make each signature attest to a
 * different set of bytes and none of them to the terms. What is hashed is `canonicalAgreement()` —
 * a deterministic serialisation of the terms and every word composed from them, with the
 * signatures excluded by construction because they are not part of it. That string is fixed from
 * the moment the agreement is issued until the end of time, every signatory records the digest
 * they saw, and the digest is recomputed from the stored snapshot on every single read. Three
 * things follow, and all three are asserted in `tools/verify-agreements.mjs`:
 *
 *   - a signed agreement whose terms are altered — by an endpoint, by a bug, or by somebody with a
 *     database prompt — reports itself as altered, on the screen and in the payload;
 *   - two parties who signed the same document provably signed the same document;
 *   - the printed page carries its own fingerprint, so two paper copies can be compared without
 *     either party having to be believed.
 *
 * ---
 *
 * Money is an integer count of minor units and a day is a `YYYY-MM-DD` string — ADR-0003. Rent is
 * not monthly: annual is this product's default and an annual tenancy is one payment for a year,
 * due once, in advance — ADR-0004 — so every sentence below is built from `termLabel`/`termSuffix`
 * and there is no "per month" anywhere in the wording. No country, no continent and no currency is
 * named; the symbol comes from `formatMoney` and the words never say what it is.
 */

import { formatDate, type PlainDate } from "./dates";
import { formatMoney, type Minor } from "./money";
import { toTerm, termLabel, termSuffix, type RentCycle } from "./cycles";
import { chargeLabel, resolveCharge, type LeaseCharge } from "./billing";

/* ------------------------------------------------------------------ the states */

/**
 * Where an agreement is, and it is a one-way road on purpose.
 *
 * `draft` is the only state in which the terms may be regenerated, because it is the only state in
 * which nobody has been shown anything. Everything after it is a document that has left the
 * building.
 *
 * There is no `signed` → `draft`, no `sent` → `draft` and no edit at any point past `draft`. The
 * two ways out of a live agreement are `cancelled` — it was withdrawn before it was finished — and
 * `superseded`, which is what a replacement does to the thing it replaces. Both keep every
 * signature that was already given, because a signature that was given was given.
 */
export const AGREEMENT_STATUSES = [
  {
    value: "draft",
    label: "Draft",
    tone: "muted",
    hint: "Generated from the tenancy and not yet sent. It can still be regenerated or discarded.",
  },
  {
    value: "sent",
    label: "Out for signature",
    tone: "info",
    hint: "Every party can see it and sign it. The terms are fixed from this point.",
  },
  {
    value: "signed",
    label: "Signed by everybody",
    tone: "success",
    hint: "Every party named on it has signed. This is the finished document.",
  },
  {
    value: "cancelled",
    label: "Withdrawn",
    tone: "warn",
    hint: "Withdrawn before it was completed. Any signatures already given are kept on the record.",
  },
  {
    value: "superseded",
    label: "Replaced",
    tone: "muted",
    hint: "A later agreement replaced this one. It is kept exactly as it stood.",
  },
] as const;

export type AgreementStatus = (typeof AGREEMENT_STATUSES)[number]["value"];

export const AGREEMENT_STATUS_VALUES = AGREEMENT_STATUSES.map((s) => s.value) as AgreementStatus[];

export function agreementStatusInfo(status: string): (typeof AGREEMENT_STATUSES)[number] {
  return AGREEMENT_STATUSES.find((s) => s.value === status) ?? AGREEMENT_STATUSES[0];
}

/** The states in which the terms are fixed and no write may touch them. */
export function isFrozen(status: string): boolean {
  return status !== "draft";
}

/** The states in which a signature may still be given. */
export function isOpenForSignature(status: string): boolean {
  return status === "sent";
}

/* ------------------------------------------------------------------- the parties */

/**
 * Who signs, and why these four.
 *
 * A letting is agreed between three parties and is routinely guaranteed by a fourth. All four are
 * here from the first day rather than the day somebody notices, because adding a role to a
 * *signed* document's vocabulary later would leave every agreement raised before it looking as
 * though a party had been left off it.
 *
 * The **agency** is the one signatory who is a member of staff. Everybody else signs through the
 * portal, which is the door built for exactly this (ADR-0087, ADR-0102): they are not users of the
 * agency, they never become users of the agency, and a signature link never becomes a way into
 * anything except the agreements they are themselves named on.
 */
export const AGREEMENT_ROLES = [
  {
    value: "agency",
    label: "Agency",
    hint: "Signed in the office, by whoever is acting for the agency.",
    /** Signed from the agency application rather than from the portal. */
    internal: true,
  },
  { value: "landlord", label: "Landlord", hint: "The owner of the unit being let.", internal: false },
  { value: "tenant", label: "Tenant", hint: "The person taking the tenancy.", internal: false },
  {
    value: "guarantor",
    label: "Guarantor",
    hint: "Stands behind the tenant's obligations. Added by hand; most tenancies have none.",
    internal: false,
  },
] as const;

export type AgreementRole = (typeof AGREEMENT_ROLES)[number]["value"];

export const AGREEMENT_ROLE_VALUES = AGREEMENT_ROLES.map((r) => r.value) as AgreementRole[];

export function agreementRoleLabel(role: string): string {
  return AGREEMENT_ROLES.find((r) => r.value === role)?.label ?? "Party";
}

export function isInternalRole(role: string): boolean {
  return AGREEMENT_ROLES.find((r) => r.value === role)?.internal ?? false;
}

/* -------------------------------------------------------------------- the terms */

/**
 * The tenancy, frozen.
 *
 * Everything the agreement says about money or time is in here and nowhere else, so that "what was
 * agreed" is one object that can be stored, hashed, printed and compared. It is deliberately flat
 * and deliberately made of primitives: it is written to a `jsonb` column and read back by code
 * that will be years newer than the code that wrote it.
 */
export interface AgreementTerms {
  /** The tenancy's own reference, so a payment quoting it can be tied to this document. */
  leaseReference: string;
  propertyName: string;
  propertyAddress: string | null;
  unitLabel: string;
  /** Free text, from the unit. Null where the agency has recorded none. */
  unitDescription: string | null;
  startDate: PlainDate;
  /** Null for an open-ended letting, which the wording then says in as many words. */
  endDate: PlainDate | null;
  /** One period's rent, never annualised. ADR-0004. */
  rentAmount: Minor;
  rentCycle: RentCycle;
  /** How many cycles make one payment. 1 is the ordinary case. */
  rentCycleEvery: number;
  paymentTerms: "advance" | "arrears";
  graceDays: number;
  depositAmount: Minor;
  charges: LeaseCharge[];
  currency: string;
  /** Anything the agency added by hand. Printed verbatim under the conditions. */
  notes: string | null;
}

/** One signatory, as the document names them. Snapshotted for the same reason the terms are. */
export interface AgreementPartySnapshot {
  role: AgreementRole;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
}

export class AgreementError extends Error {}

/* --------------------------------------------------------- reading a tenancy in */

/**
 * The tenancy's stored charges, as this file needs them.
 *
 * `leases.charges` is an untyped `jsonb` array — it has to be, because it outlives every version of
 * the code that writes it — so anything unrecognised is dropped rather than trusted onto a
 * document somebody signs. A charge with no basis, or a value that is not a whole number, is not a
 * charge; it is a row somebody's migration left behind, and printing it would put a figure on a
 * page that no invoice will ever raise.
 */
export function readCharges(stored: unknown): LeaseCharge[] {
  if (!Array.isArray(stored)) return [];
  const out: LeaseCharge[] = [];
  for (const raw of stored) {
    if (!raw || typeof raw !== "object") continue;
    const charge = raw as Record<string, unknown>;
    const basis = charge.basis;
    const frequency = charge.frequency;
    const value = Number(charge.value);
    if (basis !== "fixed" && basis !== "percent_of_rent") continue;
    if (frequency !== "once" && frequency !== "per_period") continue;
    if (!Number.isInteger(value) || value < 0) continue;
    out.push({
      type: String(charge.type ?? "other") as LeaseCharge["type"],
      ...(typeof charge.label === "string" && charge.label ? { label: charge.label } : {}),
      basis,
      value,
      frequency,
    });
  }
  return out;
}

/* ------------------------------------------------------------- what has moved */

/** One field that has moved on the tenancy since the agreement froze it. */
export interface Divergence {
  /** The key on `AgreementTerms`, so a caller can point at the field rather than at a sentence. */
  field: keyof AgreementTerms | "charges";
  label: string;
  /** What the agreement says. Already formatted — a screen must not re-derive it. */
  agreed: string;
  /** What the tenancy says now. */
  now: string;
}

/** How one term reads on a page and in a divergence line. One function, so the two cannot differ. */
function describeTerms(terms: AgreementTerms): Record<string, string> {
  const term = toTerm(terms.rentCycle, terms.rentCycleEvery);
  const money = (value: Minor) => formatMoney(value, terms.currency);
  return {
    unitLabel: `${terms.propertyName} · ${terms.unitLabel}`,
    startDate: formatDate(terms.startDate, { long: true }),
    endDate: terms.endDate ? formatDate(terms.endDate, { long: true }) : "Open-ended",
    rentAmount: `${money(terms.rentAmount)} ${termSuffix(term)}`,
    rentCycle: termLabel(term),
    paymentTerms: terms.paymentTerms === "advance" ? "In advance" : "In arrears",
    graceDays: `${terms.graceDays} ${terms.graceDays === 1 ? "day" : "days"}`,
    depositAmount: money(terms.depositAmount),
    charges: terms.charges.length
      ? terms.charges
          .map((charge) => `${chargeLabel(charge.type, charge.label)} ${money(resolveCharge(charge, terms.rentAmount))}`)
          .join("; ")
      : "None",
  };
}

/**
 * What has changed on the tenancy since this agreement froze its terms.
 *
 * Empty means the document still describes the letting. It is computed rather than stored — see
 * the note at the top of the file — and it compares **rendered** values rather than raw ones on
 * purpose: `rentCycleEvery` moving from `null` to `1` is not a change anybody agreed differently
 * about, and a divergence report that cries about it is a report nobody reads.
 */
export function divergenceOf(agreed: AgreementTerms, live: AgreementTerms): Divergence[] {
  const before = describeTerms(agreed);
  const after = describeTerms(live);
  const fields: Array<[keyof AgreementTerms | "charges", string]> = [
    ["unitLabel", "The unit"],
    ["startDate", "Starts"],
    ["endDate", "Ends"],
    ["rentAmount", "Rent"],
    ["rentCycle", "Paid"],
    ["paymentTerms", "Payable"],
    ["graceDays", "Grace period"],
    ["depositAmount", "Deposit"],
    ["charges", "Charges"],
  ];

  const out: Divergence[] = [];
  for (const [field, label] of fields) {
    if (before[field] !== after[field]) out.push({ field, label, agreed: before[field], now: after[field] });
  }
  return out;
}

/* ------------------------------------------------------- how a signature was given */

/**
 * The two ways a signature reaches this record, and why they are two things rather than one.
 *
 * **`typed` is what this product witnessed.** Somebody signed in to their own account, read the
 * document on the screen, typed their own name, and the instant, the account, the network address,
 * the browser and the fingerprint of the exact wording in front of them were all recorded as it
 * happened. Every one of those six is evidence this product generated itself and can stand behind.
 *
 * **`on_paper` is a file somebody uploaded.** A party who prefers paper prints the agreement, signs
 * it by hand and returns a scan, and the scan is kept beside the agreement. What is recorded is
 * that a file arrived, who uploaded it, when, and which day they say the paper was signed — and
 * **that is the whole of it**. The bytes in that file are not the document that was hashed: nothing
 * in a scanned page can be compared with `content_digest`, no part of it was composed here, and
 * whether the hand that wrote on it was the party's is an assertion by whoever uploaded it and not
 * a fact this product observed.
 *
 * **So the two are never merged into one "signed" state.** They are counted separately, labelled
 * separately, printed differently, and the agreement's own printed page carries a second statement
 * — `PAPER_SIGNATURE_STATEMENT` — the moment one of them appears on it. Recording an upload as
 * though it were a typed signature would cost this product the only claim it actually makes; see
 * the long note at the top of this file, and ADR-0126.
 */
export const SIGNATURE_KINDS = [
  {
    value: "typed",
    label: "Typed in the portal",
    short: "Typed",
    /** True only where the signing itself happened here, in front of this product. */
    witnessed: true,
    hint: "Signed in to their own account, typed their own name against this document's fingerprint.",
  },
  {
    value: "on_paper",
    label: "Uploaded paper copy",
    short: "On paper",
    witnessed: false,
    hint: "A file uploaded and asserted to be this agreement signed by hand. Not witnessed here.",
  },
] as const;

export type SignatureKind = (typeof SIGNATURE_KINDS)[number]["value"];

export const SIGNATURE_KIND_VALUES = SIGNATURE_KINDS.map((k) => k.value) as SignatureKind[];

/** Anything unrecognised reads as `typed`, which is what every row written before this existed is. */
export function signatureKindInfo(kind: string | null | undefined): (typeof SIGNATURE_KINDS)[number] {
  return SIGNATURE_KINDS.find((entry) => entry.value === kind) ?? SIGNATURE_KINDS[0];
}

export function signatureKindLabel(kind: string | null | undefined): string {
  return signatureKindInfo(kind).label;
}

/**
 * Did this product witness the signing, or is it holding somebody's word for it?
 *
 * The one question every screen, every payload and the printed page have to be able to answer
 * about each signature separately. It is a property of the signature, never of the agreement:
 * an agreement is not "witnessed", its signatures are, one at a time.
 */
export function isWitnessedSignature(kind: string | null | undefined): boolean {
  return signatureKindInfo(kind).witnessed;
}

/**
 * Who says the paper copy is what it claims to be.
 *
 * `party` — the person whose signature it is, signed in to their own portal account, uploaded it
 * themselves. `agency` — a member of staff uploaded it on that party's behalf, having received it
 * by post, by hand or by email.
 *
 * **The two are not the same assertion and the record must not flatten them.** A party uploading
 * their own copy asserts something about themselves, from an authenticated session of their own;
 * an agency uploading one asserts something about somebody else, and the party may never have seen
 * this system at all. Neither is witnessed here, but they are different distances from the fact.
 */
export const PAPER_SOURCES = [
  { value: "party", label: "Uploaded by the party", short: "by the party" },
  { value: "agency", label: "Uploaded by the agency", short: "by the agency" },
] as const;

export type PaperSource = (typeof PAPER_SOURCES)[number]["value"];

export const PAPER_SOURCE_VALUES = PAPER_SOURCES.map((s) => s.value) as PaperSource[];

export function paperSourceLabel(source: string | null | undefined): string {
  return PAPER_SOURCES.find((entry) => entry.value === source)?.label ?? "Uploaded";
}

/* ------------------------------------------------------------- who has signed */

export interface SignatureLike {
  role: AgreementRole;
  name: string;
  /** ISO instant, or null for somebody who has not signed. */
  signedAt: string | null;
  /** The digest of the document this person signed, or null. */
  signedDigest?: string | null;
  /** How it was given. Absent reads as `typed`, which is what every older row is. */
  signatureKind?: string | null;
}

export interface SigningState {
  total: number;
  signed: number;
  outstanding: number;
  /** True only when there is at least one party and every one of them has signed. */
  complete: boolean;
  /** Who is still to sign, by name, in the order the document names them. */
  waitingOn: string[];
  /** "1 of 3 signed" — the one line the agency chasing this needs. */
  summary: string;
  /**
   * How many of the signatures given were typed here, and how many are uploaded paper copies.
   *
   * **`signed` is the sum of these two and says nothing about their kind, which is exactly why
   * both are carried beside it.** A caller that only reads `signed` and `complete` gets the right
   * answer to "is this finished"; a caller that draws the state to a person must also say what the
   * state is made of, because "signed by everybody" over a party this product never witnessed
   * claims more than the record holds.
   */
  witnessed: number;
  onPaper: number;
  /**
   * True where at least one signature on this agreement is an uploaded paper copy.
   *
   * Written as a count comparison rather than as `.every()` over the kinds, because `.every()` is
   * true of an empty list and would report an agreement nobody has signed as fully witnessed —
   * the same trap `complete` closes two lines above, and it is closed the same way.
   */
  anyOnPaper: boolean;
  /** "1 signature is an uploaded paper copy", or null where none is. Never merged into `summary`. */
  paperSummary: string | null;
}

/**
 * Who has signed and who has not.
 *
 * **`every()` is true of an empty list, and that is the trap this function exists to close.** An
 * agreement with no parties on it would otherwise report itself complete the moment it was raised,
 * and the screen would say "Signed by everybody" over a document nobody had ever seen. So
 * completeness requires a party to exist as well as every party to have signed, and
 * `tools/verify-agreements.mjs` asserts precisely that case rather than assuming it cannot happen.
 */
export function signingState(parties: readonly SignatureLike[]): SigningState {
  const total = parties.length;
  const given = parties.filter((party) => Boolean(party.signedAt));
  const signed = given.length;
  const waitingOn = parties.filter((party) => !party.signedAt).map((party) => party.name);
  const onPaper = given.filter((party) => !isWitnessedSignature(party.signatureKind)).length;
  return {
    total,
    signed,
    outstanding: total - signed,
    complete: total > 0 && signed === total,
    waitingOn,
    summary: total === 0 ? "Nobody to sign" : `${signed} of ${total} signed`,
    witnessed: signed - onPaper,
    onPaper,
    anyOnPaper: onPaper > 0,
    paperSummary:
      onPaper === 0
        ? null
        : onPaper === 1
          ? "1 signature is an uploaded paper copy"
          : `${onPaper} signatures are uploaded paper copies`,
  };
}

/* --------------------------------------------------------------- the document */

export interface AgreementInput {
  /** The agreement's own reference. Quoted on every message about it. */
  reference: string;
  /** The day it was raised. Passed in, never read from a clock — this file is pure. */
  issuedOn: PlainDate;
  agency: AgreementPartySnapshot;
  terms: AgreementTerms;
  /** Everybody who signs, in the order the document names them. The agency is one of them. */
  parties: AgreementPartySnapshot[];
  /** The reference of the agreement this one replaces, or null. */
  supersedes: string | null;
}

export interface AgreementScheduleLine {
  label: string;
  /** What it is: a one-off at commencement, or with every rent payment. */
  when: string;
  amount: Minor;
  amountLabel: string;
}

export interface AgreementDocument {
  reference: string;
  issuedOn: PlainDate;
  issuedOnLabel: string;
  title: string;
  /** The one-line description under the title: the unit and the term. */
  subject: string;
  /** The parties, as the opening of the document names them. */
  recitals: string[];
  /** The particulars — the table of what was agreed, label and value. */
  particulars: Array<[string, string]>;
  /** Commencement and recurring charges, drawn from the tenancy's own charge list. */
  schedule: AgreementScheduleLine[];
  totals: {
    /** Payable before the keys change hands: first rent, deposit and every one-off charge. */
    commencement: Minor;
    commencementLabel: string;
    /** One period's rent and every recurring charge on it. */
    perPeriod: Minor;
    perPeriodLabel: string;
    periodSuffix: string;
  };
  /** The conditions, as a list. A paragraph nobody reads is a condition nobody agreed to. */
  conditions: string[];
  /** Whatever the agency typed on the tenancy, verbatim, or null. */
  notes: string | null;
  /** The signature blocks, in order, with nothing filled in — the signing fills them. */
  signatories: Array<{ role: AgreementRole; roleLabel: string; name: string }>;
  /** What this product says a signature here is. Printed on the page. */
  signatureStatement: string;
  supersedes: string | null;
}

/**
 * The sentence printed under the signature blocks, and the only claim this product makes.
 *
 * Read it as a promise that has to survive being quoted back: it says what was recorded and by
 * what means, and it says nothing at all about what that is worth in law, because this product
 * cannot know and must not imply. Changing it is a decision, not a copy edit — ADR-0126.
 */
export const SIGNATURE_STATEMENT =
  "Each party named above signed this agreement in the RentorNG portal by typing their own name " +
  "while signed in to their own account. The date and time, the account, the network address and " +
  "the browser used are recorded against each signature, together with the fingerprint of this " +
  "document, which is printed below. The fingerprint identifies the exact wording and figures that " +
  "were signed: a copy showing a different fingerprint is not this document.";

/**
 * The second statement, printed **only** where a signature on the document is an uploaded paper
 * copy — and printed beside `SIGNATURE_STATEMENT` rather than instead of it.
 *
 * **This is not part of the document and must never become part of it.** `SIGNATURE_STATEMENT` goes
 * through `canonicalAgreement()` and is therefore inside the fingerprint every party signed; this
 * one is layered on top at the moment of drawing, by the page and by the PDF, from the signature
 * rows. The reason is arithmetic rather than taste: the digest is stamped once and recomputed on
 * every read, so a sentence that appears when somebody uploads a scan would change the digest of an
 * agreement that was already signed, and **every signature on it would report itself as given
 * against different wording**. The same trap catches anybody tempted to reword the statement above
 * to mention paper — that edit would report every agreement ever issued as altered, all at once.
 *
 * It exists because the statement above says every party signed in the portal by typing their own
 * name. On an agreement where one of them did not, that sentence is no longer true of the whole
 * document, and a document that carries an untrue sentence about its own signatures is worse than
 * one that carries none. So this paragraph names the exception, says what was actually recorded,
 * and says plainly what was not.
 */
export const PAPER_SIGNATURE_STATEMENT =
  "Where a signature block above is marked as an uploaded paper copy, the paragraph above does not " +
  "describe it. What is recorded for that party is a file supplied to this product — by the party " +
  "themselves, or by the agency acting for them — together with who supplied it, when they supplied " +
  "it, and the day it is said to have been signed by hand. Nothing in that file was composed or " +
  "checked here: it is not the document whose fingerprint is printed below, it has not been compared " +
  "with it, and that the hand which wrote on it was that party's is stated by whoever supplied the " +
  "file rather than recorded by this product as it happened. The file is kept with this agreement " +
  "and the agency can produce it.";

/**
 * Compose the agreement.
 *
 * Throws rather than producing something approximate, and the refusals are all one refusal: **the
 * agreement may not say anything the tenancy does not.** The same discipline as `offerLetter()` in
 * `offer-letter.ts`, and for the same reason — this is the document a tenant produces two years
 * later, and the agency is the one who has to explain it.
 */
export function tenancyAgreement(input: AgreementInput): AgreementDocument {
  const { terms } = input;

  if (!input.parties.length) {
    throw new AgreementError("An agreement needs somebody to sign it.");
  }
  if (!input.parties.some((party) => party.role === "tenant")) {
    throw new AgreementError("An agreement needs a tenant. This tenancy has none recorded.");
  }
  if (!input.parties.some((party) => party.role === "agency")) {
    throw new AgreementError("An agreement is between the agency and the other parties, so the agency signs it too.");
  }
  if (terms.rentAmount <= 0) {
    throw new AgreementError("This tenancy has no rent on it, so there is nothing to agree.");
  }
  if (terms.endDate && terms.endDate < terms.startDate) {
    throw new AgreementError("This tenancy ends before it starts, so an agreement cannot be drawn from it.");
  }

  const money = (value: Minor) => formatMoney(value, terms.currency);
  const term = toTerm(terms.rentCycle, terms.rentCycleEvery);
  const suffix = termSuffix(term);
  const where = [terms.propertyName, terms.unitLabel].filter(Boolean).join(", ");
  const address = terms.propertyAddress ? `${where}, ${terms.propertyAddress}` : where;

  /* The charges, split the way they are actually collected: everything payable once lands on the
     commencement invoice with the first rent, and everything recurring is repeated with each rent
     payment. This is `buildSchedule()`'s rule restated in words; the figures are resolved with
     `resolveCharge`, which is the same function the invoices are raised through, so a percentage
     charge on the page and on the invoice cannot come out differently. */
  const once = terms.charges.filter((charge) => charge.frequency === "once");
  const recurring = terms.charges.filter((charge) => charge.frequency === "per_period");

  const schedule: AgreementScheduleLine[] = [];
  schedule.push({
    label: `Rent, ${termLabel(term).toLowerCase()}`,
    when: terms.paymentTerms === "advance" ? "Each period, in advance" : "Each period, in arrears",
    amount: terms.rentAmount,
    amountLabel: money(terms.rentAmount),
  });
  if (terms.depositAmount > 0) {
    schedule.push({
      label: "Deposit",
      when: "Once, at commencement",
      amount: terms.depositAmount,
      amountLabel: money(terms.depositAmount),
    });
  }
  for (const charge of once) {
    const amount = resolveCharge(charge, terms.rentAmount);
    schedule.push({
      label: chargeLabel(charge.type, charge.label),
      when: "Once, at commencement",
      amount,
      amountLabel: money(amount),
    });
  }
  for (const charge of recurring) {
    const amount = resolveCharge(charge, terms.rentAmount);
    schedule.push({
      label: chargeLabel(charge.type, charge.label),
      when: "With every rent payment",
      amount,
      amountLabel: money(amount),
    });
  }

  const perPeriod = terms.rentAmount + recurring.reduce((total, c) => total + resolveCharge(c, terms.rentAmount), 0);
  const commencement =
    (terms.paymentTerms === "advance" ? perPeriod : 0) +
    terms.depositAmount +
    once.reduce((total, c) => total + resolveCharge(c, terms.rentAmount), 0);

  /* ------------------------------------------------------------------ recitals */

  const recitals: string[] = [];
  const others = input.parties.filter((party) => party.role !== "agency");
  recitals.push(
    `This agreement is made on ${formatDate(input.issuedOn, { long: true })} between ` +
      `${input.agency.name}, acting as agent in respect of the property described below, and the ` +
      `${others.length === 1 ? "party" : "parties"} named below.`,
  );
  for (const party of others) {
    recitals.push(
      `${agreementRoleLabel(party.role)}: ${party.name}` +
        (party.address ? `, of ${party.address}` : "") +
        (party.phone ? `, telephone ${party.phone}` : "") +
        ".",
    );
  }
  if (input.supersedes) {
    recitals.push(
      `This agreement replaces agreement ${input.supersedes} in full. That agreement is superseded ` +
        `from the date of this one and no longer stands, whatever signatures it carries.`,
    );
  }

  /* --------------------------------------------------------------- particulars */

  const particulars: Array<[string, string]> = [
    ["Property", address],
    ["Tenancy reference", terms.leaseReference],
    ["Term begins", formatDate(terms.startDate, { long: true })],
    [
      "Term ends",
      terms.endDate
        ? formatDate(terms.endDate, { long: true })
        : "Open-ended. The tenancy continues until it is brought to an end by either party.",
    ],
    ["Rent", `${money(terms.rentAmount)} ${suffix}`],
    ["Rent payable", terms.paymentTerms === "advance" ? "In advance" : "In arrears"],
    [
      "Grace period",
      terms.graceDays > 0
        ? `${terms.graceDays} ${terms.graceDays === 1 ? "day" : "days"} after each due date`
        : "None. Rent is late the day after it falls due.",
    ],
  ];
  if (terms.depositAmount > 0) particulars.push(["Deposit", money(terms.depositAmount)]);
  if (terms.unitDescription) particulars.push(["The unit", terms.unitDescription]);

  /* ---------------------------------------------------------------- conditions */

  const conditions: string[] = [];

  /*
    The first condition is the one this product exists to get right, and it is written from the
    term rather than assumed to be a month. An annual tenancy is one payment for a year, due once,
    in advance — ADR-0004 — and an agreement that said "monthly" would be wrong for most of the
    tenancies in this product.
  */
  conditions.push(
    terms.paymentTerms === "advance"
      ? `The rent of ${money(terms.rentAmount)} is payable ${suffix}, in advance, the first payment ` +
        `falling due on ${formatDate(terms.startDate, { long: true })}.`
      : `The rent of ${money(terms.rentAmount)} is payable ${suffix}, in arrears, at the end of each ` +
        `period, the first period beginning on ${formatDate(terms.startDate, { long: true })}.`,
  );

  if (recurring.length) {
    conditions.push(
      `In addition to the rent, ${recurring
        .map((charge) => `${chargeLabel(charge.type, charge.label).toLowerCase()} of ` +
          `${money(resolveCharge(charge, terms.rentAmount))}`)
        .join(", and ")} is payable with each rent payment.`,
    );
  }

  if (once.length) {
    conditions.push(
      `The following are payable once, at commencement: ${once
        .map((charge) => `${chargeLabel(charge.type, charge.label).toLowerCase()} of ` +
          `${money(resolveCharge(charge, terms.rentAmount))}`)
        .join(", ")}.`,
    );
  }

  conditions.push(
    `${money(commencement)} is payable before the tenant takes occupation. Every payment must quote ` +
      `the reference ${terms.leaseReference} so that it is credited to this tenancy.`,
  );

  if (terms.depositAmount > 0) {
    conditions.push(
      `The deposit of ${money(terms.depositAmount)} is held on the tenant's behalf and is not rent. ` +
        `It is returned at the end of the tenancy, less any sum properly due for rent outstanding or ` +
        `for damage beyond fair wear and tear.`,
    );
  }

  conditions.push(
    terms.graceDays > 0
      ? `Rent unpaid ${terms.graceDays} ${terms.graceDays === 1 ? "day" : "days"} after its due date ` +
        `is treated as late, and the agency may apply the late payment charge shown on the tenant's ` +
        `statement of account.`
      : `Rent unpaid on its due date is treated as late, and the agency may apply the late payment ` +
        `charge shown on the tenant's statement of account.`,
  );

  conditions.push(
    `The tenant shall keep the unit in the condition in which it was received, fair wear and tear ` +
      `excepted, and shall report anything needing repair to the agency without delay.`,
    `The agency shall account to the landlord for rent collected under this agreement, and shall ` +
      `issue the tenant a receipt for every payment received.`,
    `This agreement is generated from the tenancy record it names and states the figures held on ` +
      `that record at the date shown. Any statement of account issued for this tenancy is drawn from ` +
      `the same figures.`,
    `Any change to what is agreed here is made by a new agreement signed by every party. This ` +
      `document is not altered once it has been signed.`,
  );

  const subject = terms.endDate
    ? `${where} — ${formatDate(terms.startDate)} to ${formatDate(terms.endDate)}`
    : `${where} — from ${formatDate(terms.startDate)}, open-ended`;

  return {
    reference: input.reference,
    issuedOn: input.issuedOn,
    issuedOnLabel: formatDate(input.issuedOn, { long: true }),
    title: "Tenancy agreement",
    subject,
    recitals,
    particulars,
    schedule,
    totals: {
      commencement,
      commencementLabel: money(commencement),
      perPeriod,
      perPeriodLabel: money(perPeriod),
      periodSuffix: suffix,
    },
    conditions,
    notes: terms.notes?.trim() ? terms.notes.trim() : null,
    signatories: input.parties.map((party) => ({
      role: party.role,
      roleLabel: agreementRoleLabel(party.role),
      name: party.name,
    })),
    signatureStatement: SIGNATURE_STATEMENT,
    supersedes: input.supersedes,
  };
}

/* ------------------------------------------------------------ the fingerprint */

/**
 * The exact bytes that are signed.
 *
 * Deterministic by construction rather than by hope: every value is written in a fixed order with
 * a fixed separator, and **nothing derived from the outside world goes in**. No signature, no
 * status, no timestamp of a read, no party's telephone number as it stands today — only the
 * reference, the day it was issued, and every word and figure the document states.
 *
 * `JSON.stringify` is deliberately not used on an object. Key order in a JavaScript object is
 * stable in practice and not in specification, a `Map` or a future refactor could reorder it, and
 * a fingerprint that changes because somebody tidied a field is a fingerprint that reports every
 * document as altered. A line-per-value encoding cannot do that, and it is readable in a diff when
 * two fingerprints do disagree and somebody has to find out why.
 *
 * The signatories go in through the recitals, which name each of them with the address and number
 * held for them when the agreement was raised — that is how a party is identified on a real
 * agreement, so it is part of what was agreed. It does not make the fingerprint fragile, because
 * those values are a snapshot stored on the agreement itself and are never read back from the
 * client record: a tenant who changes their telephone number next year changes nothing about a
 * document they signed last year.
 */
export function canonicalAgreement(document: AgreementDocument): string {
  const lines: string[] = [];
  const put = (key: string, value: string | number | null) => {
    lines.push(`${key}${value === null ? "" : String(value)}`);
  };

  put("version", 1);
  put("reference", document.reference);
  put("issuedOn", document.issuedOn);
  put("title", document.title);
  put("subject", document.subject);
  put("supersedes", document.supersedes);
  document.recitals.forEach((line, index) => put(`recital.${index}`, line));
  document.particulars.forEach(([label, value], index) => put(`particular.${index}`, `${label}${value}`));
  document.schedule.forEach((line, index) =>
    put(`schedule.${index}`, `${line.label}${line.when}${line.amount}`),
  );
  put("total.commencement", document.totals.commencement);
  put("total.perPeriod", document.totals.perPeriod);
  put("total.periodSuffix", document.totals.periodSuffix);
  document.conditions.forEach((line, index) => put(`condition.${index}`, line));
  put("notes", document.notes);
  document.signatories.forEach((party, index) => put(`signatory.${index}`, `${party.role}${party.name}`));
  put("statement", document.signatureStatement);

  return lines.join("\n");
}

/**
 * The fingerprint as it is shown to a person: four groups of four, upper case.
 *
 * A sixty-four character hexadecimal string is unreadable and unquotable over a telephone, and the
 * whole point of printing it is that two people holding two copies can compare them. The first
 * sixteen characters of a SHA-256 digest are ample for that: this is a comparison aid for a
 * document whose full digest is held in the record, not a security boundary, and the full digest
 * is what every check in the API actually compares.
 */
export function fingerprintOf(digest: string): string {
  const head = digest.replace(/[^0-9a-f]/gi, "").slice(0, 16).toUpperCase();
  return (head.match(/.{1,4}/g) ?? []).join(" ");
}
